import SwitchButton from "@components/buttons/SwitchButton"
import TextButton from "@components/buttons/TextButton"
import { BreadcrumbText, Headers } from "@components/Menu/common"
import { MenuSwatchProps } from "@components/MenuColorSwatches"
import { ItemContainer } from "@components/MenuItem"
import MenuItemAccordion from "@components/MenuItemAccordion"
import MenuItemColorSwatches, {
  anchorStylesSideNav,
} from "@components/MenuItemColorSwatches"
import { MenuItemTogglableCompactProps } from "@components/MenuItemTogglableCompact"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body14Styles } from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const Overlay = styled.div<{ $open?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.black};
  will-change: opacity;
  opacity: ${(props) => (props.$open ? "0.5" : "0")};
  transition: opacity ${cubicBezier} ${animation.medium};
  pointer-events: ${(props) => (props.$open ? "" : "none")};
  cursor: pointer;
  z-index: 1001;
`

const Container = styled.div`
  @media ${breakpoints.laptop} {
    display: none;
  }
`

const MenuContainer = styled.div<{ $open?: boolean }>`
  position: fixed;
  top: 0;
  left: 72px;
  right: 0;
  bottom: 0;
  max-height: 100vh;
  will-change: opacity, transform;
  opacity: ${(props) => (props.$open ? "1" : "0")};
  transform: ${(props) => (props.$open ? "translateX(0)" : "translateX(100%)")};
  transition:
    opacity ${cubicBezier} ${animation.medium},
    transform ${cubicBezier} ${animation.medium};
  pointer-events: ${(props) => (props.$open ? "" : "none")};
  z-index: 1001;
`

const Menu = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-direction: column;
  background-color: ${colors.offWhite};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const ResetButton = styled.button`
  ${body14Styles}
  height: 40px;
  padding: 0 16px;
  color: ${colors.mediumDarkGray};
  background-color: transparent;
  border: solid 1px ${colors.dividerLineGray};

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const SpecialItem = styled(ItemContainer)`
  padding: 7.5px 0 7.5px 24px;
`

const Body = styled.div`
  overflow: hidden auto;
`

const Footer = styled.div`
  padding: 20px 20px 32px;
  /* box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.1); */
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.dividerLineGray};
`

interface ProductFilterMobileProps {
  activeBrands: number
  activeColors: number
  activeDiscountTiers: number
  activeProductSeries: number
  activeSubTypes: number
  brands: Array<MenuItemTogglableCompactProps>
  colors: Array<MenuSwatchProps>
  devices: Array<MenuItemTogglableCompactProps>
  discountTiers: Array<MenuItemTogglableCompactProps>
  hasBundleProducts: boolean
  hasMagnetCompatibleProducts: boolean
  hasPrintedDesigns: boolean
  isClearance: boolean
  isCustomPrints: boolean
  isOpen: boolean
  onRequestClose: (event) => void
  onResetFilters: (event) => void
  onToggleBundleProducts: () => void
  onToggleClearance: (event) => void
  onToggleInStock: (event) => void
  onToggleMagnetCompatibleProducts: () => void
  onTogglePrintedDesigns: () => void
  productSeries: Array<MenuItemTogglableCompactProps>
  showBundleProducts: boolean
  showClearance: boolean
  showInStock: boolean
  showMagnetCompatibleProducts: boolean
  showPrintedDesigns: boolean
  subTypes: Array<MenuItemTogglableCompactProps>
}

const ProductFilterMobile: React.FC<ProductFilterMobileProps> = ({
  activeBrands,
  activeColors,
  activeDiscountTiers,
  activeProductSeries,
  activeSubTypes,
  brands,
  colors,
  devices,
  discountTiers,
  hasBundleProducts,
  hasMagnetCompatibleProducts,
  hasPrintedDesigns,
  isClearance,
  isCustomPrints,
  isOpen,
  onRequestClose,
  onResetFilters,
  onToggleBundleProducts,
  onToggleClearance,
  onToggleInStock,
  onToggleMagnetCompatibleProducts,
  onTogglePrintedDesigns,
  productSeries,
  showBundleProducts,
  showClearance,
  showInStock,
  showMagnetCompatibleProducts,
  showPrintedDesigns,
  subTypes,
}) => {
  const menuRef: any = React.useRef(null)
  useModalBackgroundClose({
    isOpen: isOpen,
    modalRef: menuRef,
    onRequestClose,
  })

  let adjustedSubTypes = subTypes
  let adjustedActiveSubTypes = activeSubTypes

  if (hasPrintedDesigns && subTypes.find(({ label }) => label === "Cases")) {
    const [cases, rest] = _.partition(
      subTypes,
      ({ label }) => label === "Cases"
    )
    adjustedSubTypes = [
      cases[0],
      {
        label: "Custom Cases",
        onClick: () => onTogglePrintedDesigns(),
        toggled: showPrintedDesigns,
      },
      ...rest,
    ].filter((f) => !!f)

    adjustedActiveSubTypes = activeSubTypes + (showPrintedDesigns ? 1 : 0)
  }

  return (
    <Container id="ProductFilterMobile">
      <Overlay $open={isOpen} onClick={onRequestClose} />
      <MenuContainer $open={isOpen} ref={menuRef}>
        <Menu id="filterMenu">
          <Headers>
            <BreadcrumbText>Filter</BreadcrumbText>
            <ResetButton onClick={onResetFilters}>Clear Filters</ResetButton>
          </Headers>
          <Body>
            {!isCustomPrints && (
              <SpecialItem onClick={onToggleInStock}>
                In Stock Only
                <SwitchButton
                  active={showInStock}
                  ariaLabel={showInStock ? "Show In Stock Only" : "Show All"}
                />
              </SpecialItem>
            )}
            {!isClearance && (
              <SpecialItem onClick={onToggleClearance}>
                Sale Only
                <SwitchButton
                  active={showClearance}
                  ariaLabel={showClearance ? "Hide Sale" : "Show Sale"}
                />
              </SpecialItem>
            )}
            {!isCustomPrints && colors.length > 1 && (
              <>
                <MenuItemColorSwatches
                  activeColorCount={activeColors}
                  anchorContainerStyles={anchorStylesSideNav}
                  anchorId="ProductFilterMobile"
                  colors={colors}
                  expandByDefault
                />
                <Divider />
              </>
            )}
            {brands.length > 1 && (
              <>
                <MenuItemAccordion
                  activeItemCount={activeBrands}
                  expandByDefault
                  items={brands}
                  label="Brand"
                />
                <Divider />
              </>
            )}
            {productSeries.length > 1 && (
              <>
                <MenuItemAccordion
                  activeItemCount={activeProductSeries}
                  expandByDefault={!isClearance}
                  items={productSeries}
                  label="Series"
                />
                <Divider />
              </>
            )}
            {(hasBundleProducts || hasMagnetCompatibleProducts) && (
              <>
                <MenuItemAccordion
                  activeItemCount={
                    showBundleProducts && showMagnetCompatibleProducts
                      ? 2
                      : showBundleProducts
                        ? 1
                        : showMagnetCompatibleProducts
                          ? 1
                          : 0
                  }
                  expandByDefault
                  items={
                    hasBundleProducts && hasMagnetCompatibleProducts
                      ? [
                          {
                            label: "Bundle Product",
                            onClick: onToggleBundleProducts,
                            toggled: showBundleProducts,
                          },
                          {
                            label: "Built-in Magnet",
                            onClick: onToggleMagnetCompatibleProducts,
                            toggled: showMagnetCompatibleProducts,
                          },
                        ]
                      : hasBundleProducts
                        ? [
                            {
                              label: "Bundle Product",
                              onClick: onToggleBundleProducts,
                              toggled: showBundleProducts,
                            },
                          ]
                        : [
                            {
                              label: "Built-in Magnet",
                              onClick: onToggleMagnetCompatibleProducts,
                              toggled: showMagnetCompatibleProducts,
                            },
                          ]
                  }
                  label="Feature"
                />
                <Divider />
              </>
            )}
            {adjustedSubTypes.length > 1 && (
              <>
                <MenuItemAccordion
                  activeItemCount={adjustedActiveSubTypes}
                  expandByDefault
                  items={adjustedSubTypes}
                  label="Type"
                />
                <Divider />
              </>
            )}
            {isCustomPrints && devices.length > 1 && (
              <>
                <MenuItemAccordion
                  expandByDefault
                  items={devices}
                  label="Device"
                />
                <Divider />
              </>
            )}
            {isClearance && (
              <>
                <MenuItemAccordion
                  activeItemCount={activeDiscountTiers}
                  expandByDefault
                  items={discountTiers}
                  label="Sale"
                />
              </>
            )}
          </Body>
          <Footer>
            <TextButton onClick={onRequestClose} text="Done" />
          </Footer>
        </Menu>
      </MenuContainer>
    </Container>
  )
}

export default ProductFilterMobile
